// =============================================================================
// FILE: packages/renderer/src/components/undo/ConfidenceModal.tsx
// PURPOSE: R2 Confidence Principle — before any major/destructive action, R2
//   shows this modal: "I am about to make N changes. Checkpoint created. You
//   can undo everything." Creates an automatic checkpoint, then proceeds.
//   Usage: import { useConfidence } from '...' and call show() before bulk ops.
// =============================================================================

import { type JSX } from 'react';
import { create } from 'zustand';
import { createPortal } from 'react-dom';
import { ShieldCheck, Undo2, AlertTriangle, X } from 'lucide-react';
import { useUndoEngine } from '../../stores/undoEngine.store.js';

// ── Store ─────────────────────────────────────────────────────────────────────
interface ConfidenceState {
  visible:     boolean;
  title:       string;
  description: string;
  changeCount: number;
  onConfirm:   (() => void) | null;
  onCancel:    (() => void) | null;
  show(opts: {
    title:       string;
    description: string;
    changeCount: number;
    onConfirm:   () => void;
    onCancel?:   () => void;
  }): void;
  dismiss(): void;
}

export const useConfidence = create<ConfidenceState>((set) => ({
  visible:     false,
  title:       '',
  description: '',
  changeCount: 0,
  onConfirm:   null,
  onCancel:    null,

  show({ title, description, changeCount, onConfirm, onCancel }) {
    set({ visible: true, title, description, changeCount, onConfirm, onCancel: onCancel ?? null });
  },

  dismiss() {
    set({ visible: false, onConfirm: null, onCancel: null });
  },
}));

// ── UI Component ──────────────────────────────────────────────────────────────
function ConfidenceModalInner(): JSX.Element {
  const { title, description, changeCount, onConfirm, onCancel, dismiss } = useConfidence();
  const createCheckpoint = useUndoEngine(s => s.createCheckpoint);

  const handleConfirm = () => {
    // Auto-create checkpoint before proceeding
    createCheckpoint(`Before: ${title}`);
    dismiss();
    onConfirm?.();
  };

  const handleCancel = () => {
    dismiss();
    onCancel?.();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9900,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(2,4,14,0.85)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      animation: 'fadeIn 0.18s ease',
    }}>
      <div data-testid="confidence-modal" style={{
        width: 'min(440px, 90vw)',
        background: 'rgba(5,10,24,0.98)',
        border: '1px solid rgba(37,181,168,0.3)',
        borderRadius: 16,
        boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 40px rgba(37,181,168,0.08)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px 16px',
          borderBottom: '1px solid rgba(30,50,80,0.5)',
          background: 'rgba(8,14,32,0.6)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: 'rgba(37,181,168,0.12)', border: '1px solid rgba(37,181,168,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#25B5A8',
          }}>
            <ShieldCheck size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#f0f4ff', marginBottom: 4 }}>
              {title}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(148,172,210,0.65)', lineHeight: 1.5 }}>
              {description}
            </div>
          </div>
          <button onClick={handleCancel} style={{ background: 'none', border: 'none', color: 'rgba(100,140,180,0.4)', cursor: 'pointer' }}>
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Change count */}
          <div style={{
            padding: '12px 16px', borderRadius: 10, marginBottom: 16,
            background: 'rgba(37,181,168,0.06)', border: '1px solid rgba(37,181,168,0.2)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <AlertTriangle size={16} style={{ color: '#fcd34d', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#dce8f8' }}>
                {changeCount > 0 ? `${changeCount} changes will be applied` : 'This action will modify your project'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(148,172,210,0.5)', marginTop: 3 }}>
                A checkpoint will be created automatically before proceeding
              </div>
            </div>
          </div>

          {/* Safety guarantee */}
          <div style={{
            padding: '12px 16px', borderRadius: 10, marginBottom: 20,
            background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <Undo2 size={14} style={{ color: '#4ade80', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: 'rgba(148,210,170,0.8)', lineHeight: 1.55 }}>
              <strong style={{ color: '#4ade80', fontWeight: 700 }}>You can undo everything.</strong>
              {' '}R2 will create a restore point before this operation. Use the History Timeline or
              {' '}Undo button to revert at any time.
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button
              data-testid="confidence-cancel"
              onClick={handleCancel}
              style={{
                padding: '12px', borderRadius: 9, cursor: 'pointer',
                background: 'transparent', border: '1px solid rgba(30,50,80,0.6)',
                color: 'rgba(148,172,210,0.6)', fontSize: 13, fontWeight: 700,
              }}
            >
              Cancel
            </button>
            <button
              data-testid="confidence-confirm"
              onClick={handleConfirm}
              style={{
                padding: '12px', borderRadius: 9, cursor: 'pointer',
                background: 'linear-gradient(135deg,rgba(37,181,168,0.25),rgba(37,181,168,0.15))',
                border: '1px solid rgba(37,181,168,0.5)',
                color: '#5eead4', fontSize: 13, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <ShieldCheck size={14} /> Proceed with Checkpoint
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}

export function ConfidenceModal(): JSX.Element | null {
  const visible = useConfidence(s => s.visible);
  if (!visible) return null;
  return createPortal(<ConfidenceModalInner />, document.body);
}
